import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { FormSubmissionsApiListResult } from "@/modules/form-center/domain/types";
import { formatDateTime } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";

type SubmissionRow = FormSubmissionsApiListResult["data"][number];

const statusLabels: Record<string, string> = {
  draft: "ຮ່າງ",
  submitted: "ສົ່ງແລ້ວ",
  in_review: "ກຳລັງກວດ",
  approved: "ອະນຸມັດ",
  rejected: "ປະຕິເສດ",
  cancelled: "ຍົກເລີກ",
};

const statusVariant: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  draft: "secondary",
  submitted: "default",
  in_review: "secondary",
  approved: "default",
  rejected: "destructive",
  cancelled: "outline",
};

type FormSubmissionsTableProps = {
  data: SubmissionRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetail?: (submissionId: string) => void;
};

export function FormSubmissionsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetail,
}: FormSubmissionsTableProps) {
  const columns: TanstackReactTable.ColumnDef<SubmissionRow>[] = [
    createSortableColumn<SubmissionRow>("submittedAt", "ເວລາສົ່ງ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.submittedAt
            ? formatDateTime(row.original.submittedAt)
            : "—"}
        </span>
      ),
    }),
    createSortableColumn<SubmissionRow>("status", "ສະຖານະ", {
      size: 120,
      cell: ({ row }) => {
        const s = row.original.status;
        return (
          <Badge variant={statusVariant[s] ?? "secondary"}>
            {statusLabels[s] ?? s}
          </Badge>
        );
      },
    }),
    {
      id: "submitter",
      header: "ຜູ້ສົ່ງ",
      size: 200,
      cell: ({ row }) => (
        <div>
          <div className="text-sm">{row.original.submitterName}</div>
          <div className="text-muted-foreground text-xs">
            {row.original.submitterEmail}
          </div>
        </div>
      ),
    },
    {
      id: "versionLabel",
      header: "ເວີຊັນ",
      size: 100,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.versionLabel}
        </span>
      ),
    },
    {
      id: "reviewedAt",
      header: "ກວດເມື່ອ",
      size: 170,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.reviewedAt
            ? formatDateTime(row.original.reviewedAt)
            : "—"}
        </span>
      ),
    },
    {
      id: "createdAt",
      header: "ເລີ່ມກອກ",
      size: 170,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    },
    ...(onViewDetail
      ? [
          {
            id: "actions",
            size: 72,
            header: "",
            cell: ({ row }) => (
              <RowActions
                actions={[
                  {
                    label: "ລາຍລະອຽດ",
                    icon: <EyeIcon className="h-4 w-4" />,
                    onClick: () => onViewDetail(row.original.id),
                  },
                ]}
                maxInline={1}
              />
            ),
          } satisfies TanstackReactTable.ColumnDef<SubmissionRow>,
        ]
      : []),
  ];

  return (
    <DataTable<SubmissionRow, unknown>
      noDataMessage="ຍັງບໍ່ມີການສົ່ງຟອມ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
